import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/adminlayout';
import { PRODUCTION_URL } from '../productionLink/productionLink';
import './night-audit.css';

const NightAudit = () => {
    const navigate = useNavigate();
    const [currentStatus, setCurrentStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        fetchStatus();
        fetchHistory();
    }, []);

    const fetchStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/night-audit/status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setCurrentStatus(data.data);
            }
        } catch (err) {
            console.error('Error fetching status:', err);
        }
    };

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/night-audit/history?limit=30`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setHistory(data.data);
            }
        } catch (err) {
            console.error('Error fetching history:', err);
        } finally {
            setLoading(false);
        }
    };

    const runNightAudit = async () => {
        if (!window.confirm('Are you sure you want to run the Night Audit for today?')) return;

        try {
            setRunning(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/night-audit/run`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    auditDate: new Date().toISOString().split('T')[0],
                    auditedBy: localStorage.getItem('username') || 'Admin'
                })
            });

            const data = await response.json();
            if (data.success) {
                alert('Night Audit completed successfully!');
                fetchStatus();
                fetchHistory();
            } else {
                alert(data.message || 'Failed to run Night Audit');
            }
        } catch (err) {
            console.error('Error running audit:', err);
            alert('Error running Night Audit');
        } finally {
            setRunning(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (datetime) => {
        if (!datetime) return '-';
        return new Date(datetime).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <AdminLayout>
            <div className="night-audit-container">
                <div className="audit-header">
                    <div className="header-left">
                        <button onClick={() => navigate(-1)} className="btn-back">← Back</button>
                        <h1>Night Audit</h1>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={runNightAudit}
                        disabled={running || currentStatus?.todayCompleted}
                    >
                        {running ? 'Running...' : currentStatus?.todayCompleted ? '✓ Today Completed' : 'Run Night Audit'}
                    </button>
                </div>

                {/* Current Day Status */}
                {currentStatus && (
                    <div className="status-section">
                        <h2>Today's Status - {formatDate(new Date())}</h2>
                        <div className="status-grid">
                            <div className="status-card rooms">
                                <div className="status-icon">🏨</div>
                                <div className="status-info">
                                    <span className="status-value">{currentStatus.occupiedRooms}/{currentStatus.totalRooms}</span>
                                    <span className="status-label">Occupied Rooms</span>
                                </div>
                            </div>
                            <div className="status-card occupancy">
                                <div className="status-icon">📊</div>
                                <div className="status-info">
                                    <span className="status-value">{currentStatus.occupancyRate}%</span>
                                    <span className="status-label">Occupancy Rate</span>
                                </div>
                            </div>
                            <div className="status-card checkins">
                                <div className="status-icon">✅</div>
                                <div className="status-info">
                                    <span className="status-value">{currentStatus.todayCheckIns}</span>
                                    <span className="status-label">Today's Check-ins</span>
                                </div>
                            </div>
                            <div className="status-card checkouts">
                                <div className="status-icon">🚪</div>
                                <div className="status-info">
                                    <span className="status-value">{currentStatus.todayCheckOuts}</span>
                                    <span className="status-label">Today's Check-outs</span>
                                </div>
                            </div>
                            <div className="status-card revenue">
                                <div className="status-icon">💰</div>
                                <div className="status-info">
                                    <span className="status-value">{formatCurrency(currentStatus.todayRevenue)}</span>
                                    <span className="status-label">Today's Revenue</span>
                                </div>
                            </div>
                            <div className="status-card pending">
                                <div className="status-icon">⏳</div>
                                <div className="status-info">
                                    <span className="status-value">{currentStatus.pendingPayments}</span>
                                    <span className="status-label">Pending Payments</span>
                                </div>
                            </div>
                        </div>
                        {currentStatus.todayCompleted && (
                            <div className="audit-complete-badge">
                                <span>✓ Night Audit Completed at {formatTime(currentStatus.lastAuditTime)}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Audit History */}
                <div className="history-section">
                    <h2>Audit History</h2>
                    {loading ? (
                        <div className="loading">Loading history...</div>
                    ) : history.length === 0 ? (
                        <div className="no-data">No audit history available</div>
                    ) : (
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Occupancy</th>
                                    <th>Revenue</th>
                                    <th>Check-ins</th>
                                    <th>Check-outs</th>
                                    <th>Audited By</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((audit, idx) => (
                                    <tr key={idx}>
                                        <td>{formatDate(audit.audit_date)}</td>
                                        <td>{formatTime(audit.end_time)}</td>
                                        <td>
                                            <span className="occupancy-badge">
                                                {audit.occupied_rooms}/{audit.total_rooms}
                                                <small>({audit.occupancy_percentage}%)</small>
                                            </span>
                                        </td>
                                        <td className="revenue-cell">{formatCurrency(audit.total_revenue)}</td>
                                        <td>{audit.total_checkins}</td>
                                        <td>{audit.total_checkouts}</td>
                                        <td>{audit.audited_by || 'System'}</td>
                                        <td>
                                            <span className={`badge ${audit.audit_status}`}>
                                                {audit.audit_status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default NightAudit;
